import { memo, useMemo } from 'react';
import { uid } from 'react-uid';
import { OverlaySpinner } from '@ankr.com/ui';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

import { DAYS_PER_WEEK, useDebounceValue } from 'modules/common';
import { globalTranslation, useTranslation } from 'modules/i18n';

import { useGetCronJobs } from '../hooks/useGetCronJobs';
import { useGetDistributedCronJobs } from '../hooks/useGetDistributedCronJobs';

interface Props {
  cronExpression: string;
}

const HOURS = new Array(24).fill(0);

export const Scheduler = memo(({ cronExpression }: Props) => {
  const { classes, cx } = useStyles();
  const { t, keys } = useTranslation(globalTranslation);

  const { start: timelineStart, end: timelineEnd } = useMemo(() => {
    const start = new Date();
    start.setHours(0, 0, 0, 0);

    const end = new Date(start);
    end.setDate(end.getDate() + DAYS_PER_WEEK);

    return { start, end };
  }, []);

  const debouncedCronExpression = useDebounceValue(cronExpression, 500);
  const [cronJobs, { isLoading }] = useGetCronJobs(
    debouncedCronExpression,
    timelineStart,
    timelineEnd,
  );

  const distributedCronJobs = useGetDistributedCronJobs(cronJobs);

  return (
    <div className={classes.root}>
      {isLoading && (
        <div className={classes.loadingOverlay}>
          <OverlaySpinner size={30} />
        </div>
      )}

      <div className={classes.days}>
        {distributedCronJobs.map((day, index) => (
          <Typography
            key={uid(day)}
            variant="body4"
            fontWeight={500}
            color={theme => theme.palette.grey[500]}
          >
            {t(keys.units.shortDay, {
              date: new Date().setDate(new Date().getDate() + index),
            })}
          </Typography>
        ))}
      </div>
      <div className={classes.timeAndSlots}>
        <div className={classes.timeRange}>
          {HOURS.map((hour, index) => (
            <Typography
              key={uid(hour, index)}
              className={classes.time}
              variant="body4"
              fontWeight={500}
              color={theme => theme.palette.grey[500]}
            >
              {String(index).padStart(2, '0')}:00
            </Typography>
          ))}
        </div>
        <div className={classes.rows}>
          {distributedCronJobs.map(({ jobs }) => (
            <div className={classes.row} key={uid(jobs)}>
              {jobs.map(slotsBlock =>
                slotsBlock.map((slotJobs, index) => (
                  <div
                    key={uid(-slotsBlock, index)}
                    className={cx(classes.slot, {
                      [classes.slotActive]: !!slotJobs.length,
                    })}
                  />
                )),
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

Scheduler.displayName = 'Scheduler';

const useStyles = makeStyles()(theme => ({
  root: {
    display: 'flex',
    background: theme.palette.background.default,
    padding: theme.spacing(4.5, 8),
    borderRadius: 18,
    position: 'relative',
    overflow: 'hidden',
  },
  loadingOverlay: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    background: theme.palette.background.default,
  },
  days: {
    display: 'flex',
    flexFlow: 'column nowrap',
    gap: theme.spacing(1.5),
    marginRight: theme.spacing(2.5),
    marginTop: theme.spacing(8.5),
  },
  timeRange: {
    display: 'flex',
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(4.5),
  },
  time: {
    maxWidth: 65,
    minWidth: 65,
  },
  timeAndSlots: {
    overflow: 'auto',
    paddingBottom: theme.spacing(4.5),
    '&::-webkit-scrollbar': {
      height: 8,
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: theme.palette.grey[300],
      borderRadius: 12,
    },
  },
  rows: {
    display: 'flex',
    flexFlow: 'column nowrap',
    gap: theme.spacing(2),
  },
  row: {
    display: 'flex',
    gap: theme.spacing(0.75),
  },
  slot: {
    minWidth: 14,
    minHeight: 14,
    borderRadius: 2,
    background: theme.palette.grey[200],
  },
  slotActive: {
    background: theme.palette.primary.main,
  },
}));
